// ag-grid columnDefs 

//import { ColDefUtil, ColDef } from 'node_modules/ag-grid-community/main';

// BackendColDef is the representation of a column in the grid, as passed from backend via API


const makeAgGridColDef = (apiCol, gridContext = {}) => {
    // pass in a BackendField instance -- possibly with nested children of BackendField instances
    // build a single column, recursively with children if any
    // gridContext is the grid_context from the backend -- some columns get their options (refData) from there, see optionsFromContext below
    const colDef = {};
    colDef['headerName'] = apiCol.label;
    // apiCol.coldef will have 'extra' stuff set in backend... attrs should match these exactly, so just pass them in 
    // should you put them up here or at the end?  here for now... no specific reason
    const colDefExtra = apiCol.extraColDef ?? {};
    Object.assign(colDef, colDefExtra);

    if (apiCol.children && Array.isArray(apiCol.children) && apiCol.children.length) {
        colDef['groupId'] = apiCol.path;
        colDef['children'] = apiCol.children.map(ch => makeAgGridColDef(ch, gridContext));
    } else {
        // FOR REGULAR (non-group) COLUMNS -- with some special types
        // -------------------------------------------
        colDef['field'] = apiCol.path;  // path
        colDef['type'] = [apiCol.data_type];  // strings of the data type, set in backend, should set as Array

        if (apiCol.data_type === '_progress_bar') {
            // TODO - set variant/color, max, segments?
            colDef['cellRenderer'] = 'progressBarComponent';
            colDef['filter'] = false;  // not sure if we want to filter by progress bar... maybe later if we can do range filtering or something? but for now just turn off filtering for this type
        }
        if (apiCol.data_type === '_progress_step') {
            // TODO - set variant/color, max, segments?
            colDef['cellRenderer'] = 'progressStepComponent';
            colDef['filter'] = false;  // not sure if we want to filter by progress bar... maybe later if we can do range filtering or something? but for now just turn off filtering for this type
        }

        if (apiCol.data_type === 'boolean') {
            colDef['cellRendererParams'] = {
                // NOTE: these should be translated on the backend and passed down
                booleanTrueLabel: apiCol.booleanTrueLabel ?? 'Yes',
                booleanFalseLabel: apiCol.booleanFalseLabel ?? 'No',
            };
        }
        // LATER colDef['backendType'] = apiCol.data_type  // my own thing... change this
        if (apiCol.optionsFromContext) {
            // big shared lookups (like all the TenantUsers) come ONCE in grid_context instead of in every column that uses them -- backend makes sure the key is there
            const contextOptions = gridContext[apiCol.optionsFromContext];
            if (contextOptions) {
                colDef['refData'] = contextOptions;  // same object for every column using this key (NOT a copy) -- these can be large, and ag-grid only reads refData
            } else {
                console.error(`column ${apiCol.path} gets its options from grid context key '${apiCol.optionsFromContext}' but that key is not in the grid context`);
            }
        } else if (apiCol.options) {  // would be a dict/obj of {value: label}
            // NEW: we might have values as objects with { label, url } for link rendering -- in that case, we want to use the label for the filter dropdown and value formatting, but pass the whole object to the cellRenderer for rendering (so it can use the url if needed)
            // might want to expand this later for other type of "rich lookups" like color-coded things, badges, etc.
            colDef['refData'] = {...apiCol.options};  // refData is automatically used for value formatting 
        }
        if (apiCol.data_type === 'company_tag_list') {
            // add special badge renderer
            colDef['cellRenderer'] = 'companyTagListRenderer';
            // colDef['filterParams'] = {
            //     cellRenderer: 'companyTagSingleRenderer',
            // };
            colDef['autoHeight'] = true;
            colDef['maxWidth'] = 200;
        }
        if (apiCol.data_type === 'spl_tag_list') {
            // add special badge renderer
            colDef['cellRenderer'] = 'splTagListRenderer';
            // colDef['filterParams'] = {
            //     cellRenderer: 'splTagSingleRenderer',
            // };
            colDef['autoHeight'] = true;
            colDef['maxWidth'] = 200;
        }
        if (apiCol.data_type === 'company_site_type') {
            // add special badge renderer
            colDef['cellRenderer'] = 'companySiteTypeRenderer';
            colDef['autoHeight'] = true;
            colDef['maxWidth'] = 200;
        }
        if (apiCol.path === 'sm_state') {
            // add special badge renderer to sm_state -- will always have this for any FlowObject list
            colDef['cellRenderer'] = 'smStateRenderer';
            colDef['filterParams'] = {
                cellRenderer: 'smStateRenderer',
            };
        }
        // SPECIAL THINGS FOR CONTACT GRID
        // -----------------------------------------
        // if (apiCol.path === 'portal_access') {
        if (['portal_access', 'portal_access_anno', 'portal_access_property'].includes(apiCol.path)) {
            // add special badge renderer to portal_access, used in Contact Grid
            colDef['cellClass'] = 'centered-cell';  // maybe make this an array?
            colDef['cellRenderer'] = 'portalAccessBadgeRenderer';
            colDef['filterParams'] = {
                cellRenderer: 'portalAccessBadgeRenderer',
                suppressSorting: true, // this should turn off sorting in the filter menu...
                // TODO - figure out how to show list of options in a more logical order... otherwise deactivated is first, open invite last (alphabetical)
            };
        }
        if (apiCol.path === '_email') {
            // add special badge renderer to show email addresses as links - used in Contact Grid
            colDef['cellRenderer'] = 'emailAddressRenderer';
        }
        if (apiCol.path === 'default_contact_for') {
            // add special badge renderer to show default contact for as a link - used in Contact Grid
            colDef['cellRenderer'] = 'defaultContactRenderer';
            colDef['filterParams'] = 'defaultContactRenderer';
        }
        if (apiCol.path === 'tenant_role_slugs') {
            // add special badge renderer to show tenant role slugs as a link - used in Contact Grid
            colDef['autoHeight'] = true;
            colDef['cellRenderer'] = 'tenantRolesRenderer';
            colDef['filterParams'] = {
                cellRenderer: 'tenantRolesRenderer',
                suppressSorting: true, // this should turn off sorting in the filter menu...
            };
        }
    }

    // HANDLE FILTERING, SORTING, GROUPING
    if (apiCol.isFilterable === false) {
        colDef['filter'] = false;
    }
    if (apiCol.isSortable === false) {
        colDef['sortable'] = false;
    }
    if (apiCol.isGroupable === false) {
        colDef['enableRowGroup'] = false;
    }
    return colDef;
}

class BackendField {  
    constructor({
        // field_name,  // field_name in backend
        path,  // path to data in the rowData -- uses dotted.path.notation:  _cf_data.something 
        db_lookup,  // lookup key in DB -- uses dunder__notation: _cf_data__something
        label,  // label to show in grid -- also have 'label_path' -- but not used now?
        label_path,  // not sure if we need this... but was used in backend
        data_type = 'string',  // string, number, date, boolean, etc
        boolean_true_label = 'Yes',  // if data_type is boolean, what to show for True
        boolean_false_label = 'No',  // if data_type is boolean, what to show for False
        options = null,  // list/dict of options, if provided locally?
        options_from_context = null,  // key in grid context with the options (instead of 'options') -- used for TenantUsers, so the list is not repeated in every column
        options_url = null,  // url to get options from, if NOT provided locally?
        coldef = null,  // extra stuff to pass to ag-grid colDef
        // backend should determine if we can filter/sort/group by this field
        is_filterable = false,  // or just pass 'filter_name' or 'filter_key' (use__dunder__syntax for this?)
        is_sortable = false,
        is_groupable = false,
        // for NESTED cols
        children = null,  // list of CHILD FIELDS if this is a nested object 
    }) {
        this.db_lookup = db_lookup;
        this.path = path;  // TEMPORARY - likely don't need field_name, path
        this.label = label;
        this.label_path = label_path;
        this.data_type = data_type;
        this.booleanTrueLabel = boolean_true_label;
        this.booleanFalseLabel = boolean_false_label;
        this.options = options;
        this.optionsFromContext = options_from_context;
        this.optionsUrl = options_url;
        this.extraColDef = coldef;
        // backend should determine if we can filter/sort/group by this field
        this.isFilterable = is_filterable;
        this.isSortable = is_sortable;
        this.isGroupable = is_groupable;
        // for NESTED cols
        this.children = Array.isArray(children) ? children.map(c => new BackendField(c)) : null;  // set as null to mean NOT a parent field/col
    }
    // GETTERS - they don't work that well with vue so just use them locally
    // METHODS 
    getAgGridColDef(gridContext = {}) {
        // pass in the grid context (from the backend grid meta), for columns that get their options from there
        return makeAgGridColDef(this, gridContext);
    }
    // make more methods for getting schema of this field for other purposes -- forms, charts?, etc.
}


/* class AgGridColDef {
    constructor({
        field,  // required
        type,  // array of strings of col types 
        // something about isLinkCol ??? to show as links, and set the link renderer?
        headerName = '',
        refData = null,  // object of {value: label} to use as options/choices
        cellRenderer = null, // string of renderer name, if used
        filterParams = null,  
        groupId = null,  // only if there are children 
        children = null,  // array of children AgGridColDef objects... or need to build them? - pass null otherwise?
        ...other
    }) {
        // set things here
        this.backendColDef = backendColDef;
        this.colDef = new ColDefUtil
    }
    // GETTERS - they don't work that well with vue so just use them locally
    
    // METHODS 
} */



export {
    BackendField,
}